import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';
import { CTA_URL } from '../mock';
import { useI18n } from '../i18n/I18nContext';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navbar() {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const NAV_ITEMS = [
    { label: t('nav.home'), to: '/' },
    { label: t('nav.services'), to: '/services' },
    { label: t('nav.about'), to: '/#rolunk' },
    { label: t('nav.references'), to: '/#referenciak' },
    { label: t('nav.contact'), to: '/#kapcsolat' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'py-3' : 'py-5'
      }`}
    >
      <div className="mx-auto max-w-7xl px-5">
        <div
          className={`flex items-center justify-between rounded-2xl pl-3 pr-3 md:pl-4 md:pr-5 py-2.5 transition-all duration-500 ${
            scrolled ? 'glass-strong' : 'glass'
          }`}
        >
          {/* Logo */}
          <Link to="/" className="group flex items-center gap-2.5 shrink-0">
            <img
              src="/logo-icon.png"
              alt="Leadeverest"
              className="h-10 w-auto object-contain transition-transform group-hover:scale-105"
            />
            <span className="hidden xl:inline font-display text-[18px] leading-none font-semibold tracking-tight text-slate-900">
              Leadeverest
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1 min-w-0">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} label={item.label} />
            ))}
          </nav>

          {/* Right side: lang + CTA */}
          <div className="flex items-center gap-2 shrink-0">
            <LanguageSwitcher />
            <a href={CTA_URL} target="_blank" rel="noreferrer" className="hidden lg:inline-block">
              <Button className="btn-primary bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl px-4 xl:px-5 py-2 h-10 text-sm xl:text-[15px]">
                <span className="hidden xl:inline">{t('nav.consultation')}</span>
                <span className="xl:hidden">{t('nav.consultation_short')}</span>
                <ArrowUpRight className="ml-1.5 h-4 w-4" />
              </Button>
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden flex h-10 w-10 items-center justify-center rounded-xl border border-blue-500/20 text-blue-600"
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden mt-2 glass-strong rounded-2xl p-4 space-y-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="block px-4 py-3 rounded-xl text-sm text-slate-700 hover:bg-blue-500/10 hover:text-slate-900 transition-colors"
              >
                {item.label}
              </Link>
            ))}
            <a href={CTA_URL} target="_blank" rel="noreferrer" className="block pt-2">
              <Button className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl">
                {t('nav.consultation')}
              </Button>
            </a>
          </div>
        )}
      </div>
    </header>
  );
}

function NavLink({ to, label }) {
  const location = useLocation();
  const active = location.pathname === to || (to === '/' && location.pathname === '/');
  const isHash = to.includes('#');
  const commonCls = `relative px-2.5 xl:px-4 py-2 text-sm rounded-xl transition-colors whitespace-nowrap ${
    active && !isHash ? 'text-blue-600' : 'text-slate-700/70 hover:text-slate-900'
  }`;
  return isHash ? (
    <a href={to} className={commonCls}>
      {label}
    </a>
  ) : (
    <Link to={to} className={commonCls}>
      {label}
      {active && <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent" />}
    </Link>
  );
}
